import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useNavigate } from 'react-router-dom';
import { countries } from 'countries-list';
import { getCode as getCountryCode } from 'country-list';
import { addSubject, deleteSubject, getSubjects } from '../../services/subjectCombinationService';
import {
  ArrowRight,
  Atom,
  BookOpenCheck,
  Calculator,
  CalendarDays,
  Dna,
  FlaskConical,
  Globe,
  Landmark,
  Languages,
  Laptop2,
  Microscope,
  Plus,
  Sparkles,
  Target,
  TrendingUp,
  Trash2,
  Flame,
  ScrollText,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import './DashboardBanner.css';
import { studentDashboardTranslations } from '../../config/StudentDashboardTranslation';
import { getMe } from '../../services/userService';
import {
  getUsageSummary,
  UsageDaySnapshot,
} from '../../services/usageService';
import {
  getCurrentTokenUsage,
  TokenUsageSummary,
} from '../../services/tokenUsageService';
import SubscriptionStatus from '../../components/subscription/SubscriptionStatus';
import SubscriptionModal from '../../components/subscription/SubscriptionModal';
import { useSubscription } from '../../hooks/useSubscription';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const predefinedSubjects = [
  'Math',
  'Science',
  'English',
  'History',
  'Geography',
  'Physics',
  'Chemistry',
  'Biology',
  'Computer Science',
  'Economics',
  'Sanskrit',
];

interface SubjectVisual {
  icon: LucideIcon;
  iconColor: string;
  iconColorDark?: string;
  iconBg: string;
  cardBg: string;
  cardBgDark: string;
  cardBorder: string;
  cardBorderDark?: string;
  cardGlow: string;
}

const subjectVisualMap: Record<string, SubjectVisual> = {
  Math: {
    icon: Calculator,
    iconColor: '#7c3aed',
    iconColorDark: '#a78bfa',
    iconBg: 'rgba(124, 58, 237, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(243, 232, 255, 0.62), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(167, 139, 250, 0.35)',
    cardBorderDark: 'rgba(167, 139, 250, 0.4)',
    cardGlow: '0 18px 30px rgba(124, 58, 237, 0.2)',
  },
  Science: {
    icon: Microscope,
    iconColor: '#0891b2',
    iconColorDark: '#22d3ee',
    iconBg: 'rgba(8, 145, 178, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(207, 250, 254, 0.72), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(34, 211, 238, 0.35)',
    cardBorderDark: 'rgba(34, 211, 238, 0.4)',
    cardGlow: '0 18px 30px rgba(8, 145, 178, 0.18)',
  },
  English: {
    icon: Languages,
    iconColor: '#0f766e',
    iconColorDark: '#2dd4bf',
    iconBg: 'rgba(15, 118, 110, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(204, 251, 241, 0.72), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(15, 118, 110, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(45, 212, 191, 0.35)',
    cardBorderDark: 'rgba(45, 212, 191, 0.4)',
    cardGlow: '0 18px 30px rgba(15, 118, 110, 0.18)',
  },
  History: {
    icon: Landmark,
    iconColor: '#92400e',
    iconColorDark: '#fbbf24',
    iconBg: 'rgba(146, 64, 14, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(254, 243, 199, 0.72), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(146, 64, 14, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(251, 191, 36, 0.38)',
    cardBorderDark: 'rgba(251, 191, 36, 0.4)',
    cardGlow: '0 18px 30px rgba(180, 83, 9, 0.2)',
  },
  Geography: {
    icon: Globe,
    iconColor: '#1d4ed8',
    iconColorDark: '#60a5fa',
    iconBg: 'rgba(29, 78, 216, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(219, 234, 254, 0.72), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(29, 78, 216, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(96, 165, 250, 0.36)',
    cardBorderDark: 'rgba(96, 165, 250, 0.4)',
    cardGlow: '0 18px 30px rgba(37, 99, 235, 0.2)',
  },
  Physics: {
    icon: Atom,
    iconColor: '#4338ca',
    iconColorDark: '#818cf8',
    iconBg: 'rgba(67, 56, 202, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(224, 231, 255, 0.72), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(129, 140, 248, 0.38)',
    cardBorderDark: 'rgba(129, 140, 248, 0.4)',
    cardGlow: '0 18px 30px rgba(79, 70, 229, 0.2)',
  },
  Chemistry: {
    icon: FlaskConical,
    iconColor: '#be123c',
    iconColorDark: '#fb7185',
    iconBg: 'rgba(190, 18, 60, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(ffe4e6, 0.8), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(190, 18, 60, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(251, 113, 133, 0.38)',
    cardBorderDark: 'rgba(251, 113, 133, 0.4)',
    cardGlow: '0 18px 30px rgba(225, 29, 72, 0.2)',
  },
  Biology: {
    icon: Dna,
    iconColor: '#166534',
    iconColorDark: '#4ade80',
    iconBg: 'rgba(22, 101, 52, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(220, 252, 231, 0.8), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(22, 101, 52, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(74, 222, 128, 0.36)',
    cardBorderDark: 'rgba(74, 222, 128, 0.4)',
    cardGlow: '0 18px 30px rgba(21, 128, 61, 0.2)',
  },
  'Computer Science': {
    icon: Laptop2,
    iconColor: '#0f172a',
    iconColorDark: '#e2e8f0',
    iconBg: 'rgba(15, 23, 42, 0.12)',
    cardBg: 'linear-gradient(135deg, rgba(226, 232, 240, 0.84), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(226, 232, 240, 0.1), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(148, 163, 184, 0.42)',
    cardBorderDark: 'rgba(148, 163, 184, 0.4)',
    cardGlow: '0 18px 30px rgba(51, 65, 85, 0.18)',
  },
  Economics: {
    icon: TrendingUp,
    iconColor: '#b45309',
    iconColorDark: '#fb923c',
    iconBg: 'rgba(180, 83, 9, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(255, 237, 213, 0.84), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(180, 83, 9, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(251, 146, 60, 0.4)',
    cardBorderDark: 'rgba(251, 146, 60, 0.4)',
    cardGlow: '0 18px 30px rgba(194, 65, 12, 0.2)',
  },
  Sanskrit: {
    icon: ScrollText,
    iconColor: '#a16207',
    iconColorDark: '#facc15',
    iconBg: 'rgba(161, 98, 7, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(254, 249, 195, 0.8), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(161, 98, 7, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(250, 204, 21, 0.4)',
    cardBorderDark: 'rgba(250, 204, 21, 0.4)',
    cardGlow: '0 18px 30px rgba(202, 138, 4, 0.2)',
  },
};

const fallbackSubjectPalettes: Omit<SubjectVisual, 'icon'>[] = [
  {
    iconColor: '#7c3aed',
    iconColorDark: '#a78bfa',
    iconBg: 'rgba(124, 58, 237, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(243, 232, 255, 0.7), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(167, 139, 250, 0.36)',
    cardBorderDark: 'rgba(167, 139, 250, 0.4)',
    cardGlow: '0 18px 30px rgba(124, 58, 237, 0.2)',
  },
  {
    iconColor: '#0891b2',
    iconColorDark: '#22d3ee',
    iconBg: 'rgba(8, 145, 178, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(207, 250, 254, 0.7), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(8, 145, 178, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(34, 211, 238, 0.36)',
    cardBorderDark: 'rgba(34, 211, 238, 0.4)',
    cardGlow: '0 18px 30px rgba(8, 145, 178, 0.18)',
  },
  {
    iconColor: '#be123c',
    iconColorDark: '#fb7185',
    iconBg: 'rgba(190, 18, 60, 0.14)',
    cardBg: 'linear-gradient(135deg, rgba(255, 228, 230, 0.8), rgba(255, 255, 255, 0.95))',
    cardBgDark: 'linear-gradient(135deg, rgba(190, 18, 60, 0.15), rgba(15, 23, 42, 0.8))',
    cardBorder: 'rgba(251, 113, 133, 0.4)',
    cardBorderDark: 'rgba(251, 113, 133, 0.4)',
    cardGlow: '0 18px 30px rgba(225, 29, 72, 0.18)',
  },
];

const getSubjectVisual = (subject: string): SubjectVisual => {
  const exactMatch = subjectVisualMap[subject];
  if (exactMatch) {
    return exactMatch;
  }

  const hash = subject.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const palette = fallbackSubjectPalettes[hash % fallbackSubjectPalettes.length];
  return {
    icon: BookOpenCheck,
    ...palette,
  };
};

const USAGE_LOOKBACK_DAYS = 7;
const TOKEN_USAGE_POLL_INTERVAL_MS = 60 * 1000;
const CREDIT_DISPLAY_SCALE = 100;
const DEFAULT_TOKEN_USAGE: TokenUsageSummary = {
  tokensUsed: 0,
  tokenLimit: 10000,
  remainingTokens: 10000,
  percentageUsed: 0,
  isLimitExceeded: false,
  periodStartsAt: new Date().toISOString(),
  periodEndsAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  hasUnlimitedCredits: false,
};

const formatCreditDisplayValue = (value: number) =>
  Math.max(0, Math.floor(value / CREDIT_DISPLAY_SCALE)).toLocaleString();

const getIsoDateKey = (value: Date | string) => {
  const date = typeof value === 'string' ? new Date(value) : value;
  return date.toISOString().split('T')[0];
};

const formatMinutesAsDuration = (minutes: number) => {
  if (minutes <= 0) {
    return '0 min';
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (hours === 0) {
    return `${remainingMinutes} min`;
  }
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

const formatUsageDayLabel = (isoDate: string) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(isoDate));

const defaultTokenUsageCopy = {
  title: 'AI credits',
  helper: 'Free credits remaining',
  limitLabel: 'Free credit cap',
  tokenLabel: 'credits',
  loadError: 'Unable to load credit usage. Please try again later.',
  dialogTitle: 'Credits exhausted',
  dialogDescription:
    'You have used all free AI credits. Please subscribe to continue using premium features.',
  dialogButton: 'Close',
};

type CountryCode = keyof typeof countries;
const DEFAULT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const inferTimezoneFromCountry = (countryName?: string) => {
  if (!countryName) return DEFAULT_TIME_ZONE;

  const isoCode = getCountryCode(countryName) as CountryCode | undefined;
  if (isoCode && countries[isoCode]) {
    const [primaryTimezone] = countries[isoCode].timezones || [];
    if (primaryTimezone) return primaryTimezone;
  }

  const normalized = countryName.trim().toLowerCase();
  const fallbackEntry = Object.values(countries).find((entry) =>
    [entry.name, ...((entry.altSpellings as string[]) || [])]
      .filter(Boolean)
      .some((label) => label.trim().toLowerCase() === normalized),
  );

  return fallbackEntry?.timezones?.[0] || DEFAULT_TIME_ZONE;
};

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

interface HighlightCard {
  title: string;
  value?: string;
  helper: string;
  icon: ReactNode;
  isClock?: boolean;
  onClick?: () => void;
}

interface UsageStatsSummary {
  totalMinutes: number;
  activeDays: number;
  streakDays: number;
}

const StudentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = studentDashboardTranslations[language];
  const tokenUsageCopy = t.tokenUsageWidget ?? defaultTokenUsageCopy;

  const [subjects, setSubjects] = useState<string[]>([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [subjectPendingRemoval, setSubjectPendingRemoval] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false);
  const [usageDays, setUsageDays] = useState<UsageDaySnapshot[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStatsSummary>({
    totalMinutes: 0,
    activeDays: 0,
    streakDays: 0,
  });
  const [usageError, setUsageError] = useState<string | null>(null);
  const [isUsageLoading, setIsUsageLoading] = useState(true);
  const [userCountry, setUserCountry] = useState('');
  const [userTimeZone, setUserTimeZone] = useState<string>(DEFAULT_TIME_ZONE);
  const hasLoadedUsageRef = useRef(false);
  const [tokenUsage, setTokenUsage] = useState<TokenUsageSummary>(DEFAULT_TOKEN_USAGE);
  const { hasActiveSubscription, isModalOpen, openSubscriptionModal, closeSubscriptionModal } = useSubscription();
  const hasShownTokenDialogRef = useRef(false);
  useEffect(() => {
    // Other initializations or empty if nothing else needed
  }, []);

  const loadUsageSummary = useCallback(async () => {
    if (!hasLoadedUsageRef.current) {
      setIsUsageLoading(true);
    }
    try {
      const response = await getUsageSummary(USAGE_LOOKBACK_DAYS);
      setUsageDays(response.days);
      setUsageStats({
        totalMinutes: response.totalMinutes,
        activeDays: response.activeDays,
        streakDays: response.streakDays,
      });
      setUsageError(null);
      hasLoadedUsageRef.current = true;
    } catch (error) {
      console.error('Failed to fetch usage summary:', error);
      setUsageError(t.digitalWellbeing.loadError);
    } finally {
      setIsUsageLoading(false);
    }
  }, [t.digitalWellbeing.loadError]);

  const fetchTokenUsage = useCallback(async () => {
    try {
      const summary = await getCurrentTokenUsage();
      setTokenUsage(summary);

      if (summary.isLimitExceeded && !hasActiveSubscription) {
        if (!hasShownTokenDialogRef.current) {
          hasShownTokenDialogRef.current = true;
          openSubscriptionModal();
        }
      } else {
        hasShownTokenDialogRef.current = false;
      }
    } catch (error) {
      console.error('Failed to load token usage:', error);
      // Don't block features if token usage API fails
    }
  }, [hasActiveSubscription, openSubscriptionModal]);

  useEffect(() => {
    loadUsageSummary();
  }, [loadUsageSummary]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void loadUsageSummary();
    }, TOKEN_USAGE_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [loadUsageSummary]);

  useEffect(() => {
    fetchTokenUsage();
    const intervalId = window.setInterval(fetchTokenUsage, TOKEN_USAGE_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [fetchTokenUsage]);

  const todayUsageMinutes = useMemo(() => {
    const todayKey = getIsoDateKey(new Date());
    const entry = usageDays.find((day) => getIsoDateKey(day.date) === todayKey);
    return entry?.totalMinutes ?? 0;
  }, [usageDays]);

  const todaysUsageLabel = todayUsageMinutes > 0 ? formatMinutesAsDuration(todayUsageMinutes) : '';
  const todaysUsageDisplay = todaysUsageLabel || t.digitalWellbeing.noDataLabel;
  const totalUsageLabel = formatMinutesAsDuration(usageStats.totalMinutes);
  const isTokenLimitReached = tokenUsage.isLimitExceeded;

  // Guard for Smart Chat - checks token limit
  const guardTokenLimitedAction = useCallback(
    (action: () => void) => {
      if (isTokenLimitReached && !hasActiveSubscription) {
        openSubscriptionModal();
        return;
      }
      action();
    },
    [isTokenLimitReached, hasActiveSubscription, openSubscriptionModal],
  );

  // Guard for other features - requires subscription
  const guardSubscriptionAction = useCallback(
    (action: () => void) => {
      if (!hasActiveSubscription) {
        openSubscriptionModal();
        return;
      }
      action();
    },
    [hasActiveSubscription, openSubscriptionModal],
  );

  const usageChartData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: usageDays.map((day) => formatUsageDayLabel(day.date)),
      datasets: [
        {
          label: t.digitalWellbeing.chartDatasetLabel,
          data: usageDays.map((day) => Number((day.totalMinutes / 60).toFixed(2))),
          backgroundColor: 'rgba(59, 130, 246, 0.65)',
          borderColor: '#3b82f6',
          borderWidth: 1.5,
          borderRadius: 6,
        },
      ],
    }),
    [usageDays, t.digitalWellbeing.chartDatasetLabel],
  );

  const usageChartOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<'bar'>) => {
              const value = typeof context.parsed.y === 'number' ? context.parsed.y : 0;
              return `${t.digitalWellbeing.chartDatasetLabel}: ${value.toFixed(2)}h`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(15, 23, 42, 0.08)' },
          ticks: {
            callback: (value: number | string) => `${value}h`,
          },
        },
        x: {
          grid: { display: false },
        },
      },
    }),
    [t.digitalWellbeing.chartDatasetLabel],
  );

  const usageListEntries = useMemo(
    () =>
      usageDays.map((day) => ({
        date: day.date,
        label: formatUsageDayLabel(day.date),
        totalMinutes: day.totalMinutes,
        sessionsCount: day.sessionsCount,
      })),
    [usageDays],
  );

  const hasUsageData = useMemo(
    () => usageDays.some((day) => day.totalMinutes > 0),
    [usageDays],
  );

  useEffect(() => {
    const fetchSubjects = async () => {
      setIsLoading(true);
      try {
        const fetchedSubjects = await getSubjects();
        setSubjects(fetchedSubjects.subjects || []);
        setError(null);
      } catch (err) {
        setError(t.snackbarMessages.loadFail);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubjects();
  }, []); // fetch once; error message reflects language at fetch time

  useEffect(() => {
    let isMounted = true;

    const fetchUserProfile = async () => {
      try {
        const profile = await getMe();
        if (!isMounted) return;

        setUserCountry(profile.country || '');
        setUserTimeZone(inferTimezoneFromCountry(profile.country));
      } catch (err) {
        console.error('Failed to load user profile for local time widget:', err);
      }
    };

    fetchUserProfile();

    return () => {
      isMounted = false;
    };
  }, []);



  const subjectHighlights: HighlightCard[] = useMemo(
    () => [
      {
        title: t.subjectHighlights[0].title,
        value: subjects.length.toString(),
        helper: t.subjectHighlights[0].helper,
        icon: <BookOpenCheck size={22} />,
      },
      {
        title: t.digitalWellbeing.highlightTitle,
        value: todaysUsageDisplay,
        helper: t.digitalWellbeing.highlightHelper,
        icon: <Target size={22} />,
        onClick: () => setIsUsageDialogOpen(true),
      },
      {
        title: t.subjectHighlights[1].title,
        value: `${usageStats.streakDays} ${t.subjectHighlights[1]?.value?.split(' ')[1] || 'days'}`,
        helper: t.subjectHighlights[1].helper,
        icon: <Flame size={22} />,
      },
    ],
    [subjects.length, language, todaysUsageDisplay, usageStats.streakDays, t],
  );

  const renderHighlightBody = (highlight: HighlightCard) => (
    <CardContent>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'rgba(90, 75, 252, 0.12)',
            color: 'primary.dark',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {highlight.icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {highlight.title}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {highlight.value || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {highlight.helper}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  );

  const handleSubjectSelection = (event: SelectChangeEvent<string>) => {
    setSelectedSubject(event.target.value);
    setError(null);
  };

  const handleAddSubject = async () => {
    const subjectToAdd =
      selectedSubject === 'Other' ? customSubject.trim() : selectedSubject.trim();

    if (!subjectToAdd) {
      setError(t.snackbarMessages.selectError);
      return;
    }

    if (subjects.includes(subjectToAdd)) {
      setError(t.snackbarMessages.duplicateError);
      return;
    }

    try {
      await addSubject(subjectToAdd);
      setSubjects((prev) => [...prev, subjectToAdd]);
      setSelectedSubject('');
      setCustomSubject('');
      setIsAddDialogOpen(false);
      setSnackbar({
        open: true,
        message: t.snackbarMessages.addSuccess(subjectToAdd),
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: err?.message || t.snackbarMessages.addFail,
      });
    }
  };

  const handleDeleteSubject = async () => {
    if (!subjectPendingRemoval) {
      return;
    }

    try {
      await deleteSubject(subjectPendingRemoval);
      setSubjects((prev) => prev.filter((subject) => subject !== subjectPendingRemoval));
      setSnackbar({
        open: true,
        message: t.snackbarMessages.removeInfo(subjectPendingRemoval),
        severity: 'info',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to delete subject',
        severity: 'error',
      });
    } finally {
      setSubjectPendingRemoval(null);
    }
  };

  const handleSubjectClick = useCallback(
    (subject: string) => {
      guardTokenLimitedAction(() => navigate('/chat', { state: { subject } }));
    },
    [guardTokenLimitedAction, navigate],
  );

  return (
    <Stack spacing={4}>
      <Card
        sx={(theme) => ({
          overflow: 'hidden',
          position: 'relative',
          background: theme.palette.mode === 'dark'
            ? '#0b1220'
            : 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 50%, #e0f2fe 100%)',
          color: theme.palette.mode === 'dark' ? 'common.white' : '#1e293b',
          borderRadius: '20px',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 32px rgba(0, 0, 0, 0.5)'
            : '0 10px 30px rgba(17, 24, 39, 0.35)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 18px 40px rgba(0, 0, 0, 0.6)'
              : '0 18px 40px rgba(17, 24, 39, 0.45)',
          },
        })}
      >
        {/* Floating AI Study Icons background */}
        <div className="banner-bubbles">
          <div className="banner-bubble banner-icon">🚀</div>
          <div className="banner-bubble banner-icon">🧠</div>
          <div className="banner-bubble banner-icon">📚</div>
          <div className="banner-bubble banner-icon">🔥</div>
          <div className="banner-bubble banner-icon">🎓</div>
          <div className="banner-bubble banner-icon">💙</div>
        </div>
        <CardContent sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center">
            <Box flex={1}>
              <Typography variant="overline" sx={(theme) => ({ letterSpacing: 2, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.72)' : '#64748b' })}>
                {t.welcome}
              </Typography>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                {t.missionControl}
              </Typography>
              <Typography variant="body1" sx={(theme) => ({ maxWidth: 520, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.82)' : '#475569' })}>
                {t.missionDescription}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={4}>
                <Button
                  variant="contained"
                  sx={{ background: 'linear-gradient(90deg, #0f2a44, #3b82f6)' }}
                  endIcon={<ArrowRight size={18} />}
                  onClick={() => guardTokenLimitedAction(() => navigate('/mock'))}
                >
                  {t.jumpIntoMock}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => guardTokenLimitedAction(() => setIsAddDialogOpen(true))}
                  startIcon={<Plus size={18} />}
                  sx={(theme) => ({
                    color: theme.palette.mode === 'dark' ? 'white' : '#1e293b',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(30,41,59,0.3)',
                  })}
                >
                  {t.addNewSubject}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {subjectHighlights.map((highlight) => (
          <Grid item xs={12} md={4} key={highlight.title}>
            <Card sx={highlight.onClick ? { cursor: 'pointer' } : undefined}>
              {highlight.onClick ? (
                <CardActionArea onClick={highlight.onClick}>
                  {renderHighlightBody(highlight)}
                </CardActionArea>
              ) : (
                renderHighlightBody(highlight)
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardHeader
              title={t.mySubjectsTitle}
              subheader={t.mySubjectsSubheader}
              action={
                <Button
                  variant="contained"
                  sx={{ background: 'linear-gradient(90deg, #0f2a44, #3b82f6)' }}
                  startIcon={<Plus size={18} />}
                  onClick={() => guardTokenLimitedAction(() => setIsAddDialogOpen(true))}
                >
                  {t.addSubjectButton}
                </Button>
              }
            />
            <Divider />
            <CardContent>

              {isLoading ? (
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Skeleton
                        variant="rounded"
                        sx={{ borderRadius: 3 }}
                        height={110}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : subjects.length === 0 ? (
                <Stack
                  spacing={3}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    py: 6,
                    textAlign: 'center',
                    border: '1px dashed rgba(90, 75, 252, 0.24)',
                    borderRadius: 4,
                    bgcolor: 'rgba(90, 75, 252, 0.04)',
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      bgcolor: 'rgba(90, 75, 252, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.dark',
                    }}
                  >
                    <Sparkles size={28} />
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t.letsBuildCurriculumTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.letsBuildCurriculumDesc}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    sx={{ background: 'linear-gradient(90deg, #0f2a44, #3b82f6)' }}
                    startIcon={<Plus size={18} />}
                    onClick={() => guardTokenLimitedAction(() => setIsAddDialogOpen(true))}
                  >
                    {t.addSubjectButton}
                  </Button>
                </Stack>
              ) : (
                <Grid container spacing={{ xs: 2, md: 2.5 }}>
                  {subjects.map((subject) => {
                    const subjectVisual = getSubjectVisual(subject);
                    const SubjectIcon = subjectVisual.icon;

                    return (
                      <Grid item xs={12} sm={6} key={subject}>
                        <Card
                          variant="outlined"
                          sx={(theme) => ({
                            borderRadius: 3,
                            cursor: 'pointer',
                            borderColor: theme.palette.mode === 'dark' ? (subjectVisual.cardBorderDark || subjectVisual.cardBorder) : subjectVisual.cardBorder,
                            background: theme.palette.mode === 'dark' ? subjectVisual.cardBgDark : subjectVisual.cardBg,
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              borderColor: theme.palette.mode === 'dark' ? (subjectVisual.iconColorDark || subjectVisual.iconColor) : subjectVisual.iconColor,
                              boxShadow: subjectVisual.cardGlow,
                            },
                          })}
                          onClick={() => handleSubjectClick(subject)}
                        >
                          <CardContent>
                            <Stack spacing={2}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Box
                                    sx={(theme) => ({
                                      width: 42,
                                      height: 42,
                                      borderRadius: 2.5,
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : subjectVisual.iconBg,
                                      color: theme.palette.mode === 'dark' ? (subjectVisual.iconColorDark || subjectVisual.iconColor) : subjectVisual.iconColor,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    })}
                                  >
                                    <SubjectIcon size={20} />
                                  </Box>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                      {subject}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Tap to start learning
                                    </Typography>
                                  </Box>
                                </Stack>
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSubjectPendingRemoval(subject);
                                  }}
                                >
                                  <Trash2 size={18} />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            <Card>
              <CardHeader
                title={t.aiStudyNavigatorTitle}
                subheader={t.aiStudyNavigatorSubheader}
              />
              <Divider />
              <CardContent>
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t.adaptiveMockSuggestionTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.adaptiveMockSuggestionDesc}
                    </Typography>
                    <Button
                      variant="text"
                      endIcon={<ArrowRight size={16} />}
                      sx={{ mt: 1 }}
                      onClick={() => guardTokenLimitedAction(() => navigate('/mock'))}
                    >
                      {t.launchMockStudio}
                    </Button>
                  </Box>
                  <Divider />
                  {/* <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Micro revision block
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      20-minute concept refresher recommended by the tutor engine for your weakest 10%.
                    </Typography>
                    <Button
                      variant="text"
                      endIcon={<ArrowRight size={16} />}
                      sx={{ mt: 1 }}
                      onClick={() => navigate('/your-exams')}
                    >
                      Review exam topics
                    </Button>
                  </Box> */}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {!hasActiveSubscription && (
        <Box
          sx={(theme) => ({
            position: 'fixed',
            right: { xs: 16, sm: 32 },
            bottom: { xs: 16, sm: 32 },
            width: { xs: 'calc(100% - 32px)', sm: 240 },
            maxWidth: 320,
            zIndex: 1300,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.95)' : '#e6f4ea',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
            backdropFilter: 'blur(10px)',
            borderRadius: 999,
            p: 1.25,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 6px 18px rgba(0, 0, 0, 0.4)'
              : '0 6px 18px rgba(16, 185, 129, 0.25)',
          })}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={(theme) => ({
                flexGrow: 1,
                height: 8,
                borderRadius: 999,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#d1d5db',
                overflow: 'hidden',
              })}
            >
              <Box
                sx={(theme) => ({
                  height: '100%',
                  width: `${Math.min(100, tokenUsage.percentageUsed)}%`,
                  bgcolor: theme.palette.mode === 'dark' ? '#3b82f6' : '#111827',
                  borderRadius: 999,
                  transition: 'width 0.3s ease',
                })}
              />
            </Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={(theme) => ({
                whiteSpace: 'nowrap',
                color: theme.palette.mode === 'dark' ? '#f1f5f9' : 'text.primary',
              })}
            >
              {formatCreditDisplayValue(tokenUsage.tokensUsed)}/{formatCreditDisplayValue(tokenUsage.tokenLimit)} {tokenUsageCopy.tokenLabel}
            </Typography>
          </Stack>
        </Box>
      )}

      <Dialog
        open={isUsageDialogOpen}
        onClose={() => setIsUsageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t.digitalWellbeing.dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t.digitalWellbeing.dialogDescription}
          </Typography>
          {usageError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {usageError}
            </Alert>
          )}
          {isUsageLoading ? (
            <Stack mt={3} spacing={2}>
              <Skeleton variant="rounded" height={240} />
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="55%" />
            </Stack>
          ) : hasUsageData ? (
            <>
              <Box mt={3} sx={{ height: 280 }}>
                <Bar data={usageChartData} options={usageChartOptions} />
              </Box>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                mt={3}
              >
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t.digitalWellbeing.totalLabel}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {totalUsageLabel}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t.digitalWellbeing.activeDaysLabel}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {usageStats.activeDays}/{USAGE_LOOKBACK_DAYS}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t.digitalWellbeing.streakLabel}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {t.digitalWellbeing.streakValue(usageStats.streakDays)}
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={1.2} mt={3}>
                {usageListEntries.map((entry) => (
                  <Stack
                    key={entry.date}
                    direction="row"
                    justifyContent="space-between"
                    sx={{ borderBottom: '1px solid rgba(15, 23, 42, 0.08)', py: 0.75 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>{entry.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t.digitalWellbeing.sessionCountLabel(entry.sessionsCount)}
                      </Typography>
                    </Box>
                    <Typography color="text.secondary">
                      {formatMinutesAsDuration(entry.totalMinutes)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : (
            <Stack
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{
                mt: 3,
                py: 5,
                border: '1px dashed rgba(90, 75, 252, 0.3)',
                borderRadius: 3,
                textAlign: 'center',
              }}
            >
              <Target size={32} color="#5a4bfc" />
              <Box>
                <Typography variant="h6">{t.digitalWellbeing.emptyStateTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.digitalWellbeing.emptyStateDescription}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsUsageDialogOpen(false)}>
            {t.digitalWellbeing.closeButton}
          </Button>
        </DialogActions>
      </Dialog>

      <SubscriptionModal
        isOpen={isModalOpen}
        onClose={closeSubscriptionModal}
        title="Upgrade to Pro"
        message="You've reached your free credit limit. Subscribe to unlock unlimited credits and premium features!"
      />

      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t.dialogText.addTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="subject-select-label">{t.dialogText.selectSubjectLabel}</InputLabel>
              <Select
                labelId="subject-select-label"
                label={t.dialogText.selectSubjectLabel}
                value={selectedSubject}
                onChange={handleSubjectSelection}
              >
                <MenuItem value="">
                  <em>{t.dialogText.chooseFromList}</em>
                </MenuItem>
                {predefinedSubjects.map((subject) => {
                  const subjectVisual = getSubjectVisual(subject);
                  const SubjectIcon = subjectVisual.icon;

                  return (
                    <MenuItem key={subject} value={subject}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={(theme) => ({
                            width: 24,
                            height: 24,
                            borderRadius: 1.5,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : subjectVisual.iconBg,
                            color: theme.palette.mode === 'dark' ? (subjectVisual.iconColorDark || subjectVisual.iconColor) : subjectVisual.iconColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          })}
                        >
                          <SubjectIcon size={14} />
                        </Box>
                        <Typography variant="body2">{subject}</Typography>
                      </Stack>
                    </MenuItem>
                  );
                })}
                <MenuItem value="Other">{t.dialogText.otherOption}</MenuItem>
              </Select>
            </FormControl>

            {selectedSubject === 'Other' && (
              <TextField
                label={t.dialogText.customSubjectLabel}
                placeholder={t.dialogText.customSubjectPlaceholder}
                value={customSubject}
                onChange={(event) => setCustomSubject(event.target.value)}
                fullWidth
              />
            )}

            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            <Alert severity="info">
              {t.dialogText.subjectsInfo}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => {
              setIsAddDialogOpen(false);
              setSelectedSubject('');
              setCustomSubject('');
              setError(null);
            }}
          >
            {t.dialogText.cancel}
          </Button>
          <Button variant="contained" sx={{ background: 'linear-gradient(90deg, #0f2a44, #3b82f6)' }} onClick={handleAddSubject}>
            {t.dialogText.saveSubject}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(subjectPendingRemoval)}
        onClose={() => setSubjectPendingRemoval(null)}
      >
        <DialogTitle>{t.dialogText.removeTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t.dialogText.removeDescriptionPart1}{' '}
            <strong>{subjectPendingRemoval}</strong> {t.dialogText.removeDescriptionPart2}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubjectPendingRemoval(null)}>{t.dialogText.keep}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteSubject}>
            {t.dialogText.remove}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default StudentDashboard;
